import React from 'react';

export default function BiometricDiagram({ task }) {
  const isSelfie = task.stage === 'Present_Neutral';
  const taskId = task.id;
  const isUp = taskId === 'pose_1';
  const isDown = taskId === 'pose_2';
  const isLeft = taskId === 'pose_3';
  const isRight = taskId === 'pose_4';
  const isUpLeft = taskId === 'pose_5';
  const isUpRight = taskId === 'pose_6';
  const isDownLeft = taskId === 'pose_7';
  const isDownRight = taskId === 'pose_8';

  return (
    <div className="biometric-diagram-wrap">
      {/* Top Technical Telemetry Bar */}
      <div className="diagram-telemetry-header">
        <div className="telemetry-badge-left">
          <span className="telemetry-pulse-dot" />
          <span className="telemetry-title">BIOMETRIC 3D ALIGNMENT GUIDE</span>
        </div>
        <div className="telemetry-badge-right">
          {isSelfie && <span>TARGET: 0.0° PITCH • 0.0° YAW</span>}
          {isUp && <span>TARGET: +20.0° PITCH (UPWARD)</span>}
          {isDown && <span>TARGET: -20.0° PITCH (DOWNWARD)</span>}
          {isLeft && <span>TARGET: +25.0° YAW (LEFT TURN)</span>}
          {isRight && <span>TARGET: -25.0° YAW (RIGHT TURN)</span>}
          {isUpLeft && <span>PITCH +20° ▲ • YAW +25° ◀</span>}
          {isUpRight && <span>PITCH +20° ▲ • YAW -25° ▶</span>}
          {isDownLeft && <span>PITCH -20° ▼ • YAW +25° ◀</span>}
          {isDownRight && <span>PITCH -20° ▼ • YAW -25° ▶</span>}
        </div>
      </div>

      {/* Center SVG Vector Reticle Canvas */}
      <div className="diagram-canvas-box">
        <svg
          viewBox="0 0 280 180"
          className="diagram-svg"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            {/* Grid Pattern */}
            <pattern id="bioGrid" width="20" height="20" patternUnits="userSpaceOnUse">
              <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(148, 163, 184, 0.07)" strokeWidth="1" />
            </pattern>
            {/* Cyan Glow */}
            <filter id="glowCyan" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="4" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
            {/* Emerald Glow */}
            <filter id="glowGreen" x="-20%" y="-20%" width="140%" height="140%">
              <feGaussianBlur stdDeviation="5" result="blur" />
              <feComposite in="SourceGraphic" in2="blur" operator="over" />
            </filter>
            {/* Arrow Marker */}
            <marker id="arrowHead" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
              <path d="M 0 0 L 6 3 L 0 6 z" fill="#38bdf8" />
            </marker>
            <marker id="arrowHeadGreen" markerWidth="6" markerHeight="6" refX="4" refY="3" orient="auto">
              <path d="M 0 0 L 6 3 L 0 6 z" fill="#10b981" />
            </marker>
          </defs>

          {/* Background Grid */}
          <rect width="280" height="180" fill="url(#bioGrid)" />

          {/* Corner Framing Brackets */}
          <path d="M 20 28 L 20 18 L 30 18" stroke="#0ea5e9" strokeWidth="2" fill="none" opacity="0.6" />
          <path d="M 260 28 L 260 18 L 250 18" stroke="#0ea5e9" strokeWidth="2" fill="none" opacity="0.6" />
          <path d="M 20 152 L 20 162 L 30 162" stroke="#0ea5e9" strokeWidth="2" fill="none" opacity="0.6" />
          <path d="M 260 152 L 260 162 L 250 162" stroke="#0ea5e9" strokeWidth="2" fill="none" opacity="0.6" />

          {/* 1. FRONTAL SELFIE RETICLE (Selfies 1 to 5) */}
          {isSelfie && (
            <g>
              {/* Outer Biometric Tracking Oval */}
              <ellipse
                cx="140"
                cy="90"
                rx="50"
                ry="64"
                fill="none"
                stroke="#38bdf8"
                strokeWidth="2"
                strokeDasharray="6 3"
                opacity="0.9"
                filter="url(#glowCyan)"
              />
              <ellipse
                cx="140"
                cy="90"
                rx="42"
                ry="55"
                fill="rgba(14, 165, 233, 0.04)"
                stroke="#0284c7"
                strokeWidth="1"
                opacity="0.5"
              />

              {/* Eye-level Horizontal Reference Line */}
              <line x1="80" y1="78" x2="200" y2="78" stroke="#38bdf8" strokeWidth="1.2" strokeDasharray="3 3" opacity="0.8" />
              <text x="68" y="81" fill="#38bdf8" fontSize="8" fontWeight="700" letterSpacing="0.05em" textAnchor="end">
                EYE LEVEL
              </text>

              {/* Vertical Symmetry Axis */}
              <line x1="140" y1="24" x2="140" y2="156" stroke="#0284c7" strokeWidth="1" strokeDasharray="4 4" opacity="0.4" />

              {/* Central Target Crosshair */}
              <path d="M 132 90 L 148 90 M 140 82 L 140 98" stroke="#38bdf8" strokeWidth="1.5" />
              <circle cx="140" cy="90" r="3" fill="#38bdf8" />

              {/* Abstract Facial Landmarks */}
              <circle cx="122" cy="78" r="3.5" fill="none" stroke="#e0f2fe" strokeWidth="1.5" />
              <circle cx="158" cy="78" r="3.5" fill="none" stroke="#e0f2fe" strokeWidth="1.5" />
              <path d="M 138 92 L 140 98 L 142 92" fill="none" stroke="#bae6fd" strokeWidth="1.2" />
              <path d="M 128 116 Q 140 124 152 116" fill="none" stroke="#bae6fd" strokeWidth="1.5" strokeLinecap="round" />

              {/* Eye Level Alignment Cross indicators */}
              <path d="M 85 75 L 85 81 M 82 78 L 88 78" stroke="#38bdf8" strokeWidth="1.2" />
              <path d="M 195 75 L 195 81 M 192 78 L 198 78" stroke="#38bdf8" strokeWidth="1.2" />
            </g>
          )}

          {/* 2. POSE 1: LOOK UP (Pitch +20°) */}
          {isUp && (
            <g>
              <ellipse cx="140" cy="100" rx="46" ry="58" fill="rgba(14, 165, 233, 0.05)" stroke="#0ea5e9" strokeWidth="1.5" strokeDasharray="4 2" />
              {/* Upward Vector Arrow & Arc */}
              <path d="M 140 100 L 140 32" stroke="#10b981" strokeWidth="3" markerEnd="url(#arrowHeadGreen)" filter="url(#glowGreen)" />
              <path d="M 120 75 Q 140 45 160 75" fill="none" stroke="#10b981" strokeWidth="1.5" strokeDasharray="2 2" />
              {/* Uplifted Eyeline */}
              <line x1="94" y1="88" x2="186" y2="88" stroke="#38bdf8" strokeWidth="1.2" strokeDasharray="3 2" />
              <circle cx="124" cy="86" r="3.5" fill="none" stroke="#e0f2fe" strokeWidth="1.5" />
              <circle cx="156" cy="86" r="3.5" fill="none" stroke="#e0f2fe" strokeWidth="1.5" />
              <text x="140" y="24" fill="#10b981" fontSize="10" fontWeight="800" textAnchor="middle">
                ▲ TILT CHIN UP (+20°)
              </text>
            </g>
          )}

          {/* 3. POSE 2: LOOK DOWN (Pitch -20°) */}
          {isDown && (
            <g>
              <ellipse cx="140" cy="78" rx="46" ry="58" fill="rgba(14, 165, 233, 0.05)" stroke="#0ea5e9" strokeWidth="1.5" strokeDasharray="4 2" />
              {/* Downward Vector Arrow & Arc */}
              <path d="M 140 76 L 140 148" stroke="#10b981" strokeWidth="3" markerEnd="url(#arrowHeadGreen)" filter="url(#glowGreen)" />
              <path d="M 120 105 Q 140 135 160 105" fill="none" stroke="#10b981" strokeWidth="1.5" strokeDasharray="2 2" />
              {/* Downcast Eyeline */}
              <line x1="94" y1="74" x2="186" y2="74" stroke="#38bdf8" strokeWidth="1.2" strokeDasharray="3 2" />
              <circle cx="124" cy="74" r="3.5" fill="none" stroke="#e0f2fe" strokeWidth="1.5" />
              <circle cx="156" cy="74" r="3.5" fill="none" stroke="#e0f2fe" strokeWidth="1.5" />
              <text x="140" y="166" fill="#10b981" fontSize="10" fontWeight="800" textAnchor="middle">
                ▼ TILT CHIN DOWN (-20°)
              </text>
            </g>
          )}

          {/* 4. POSE 3: TURN LEFT (Yaw +25°) */}
          {isLeft && (
            <g>
              {/* Base Head Outline Profile Left */}
              <ellipse cx="150" cy="90" rx="46" ry="60" fill="rgba(14, 165, 233, 0.05)" stroke="#0ea5e9" strokeWidth="1.5" strokeDasharray="4 2" />
              {/* Rotation Sweep Arc & Vector Left */}
              <path d="M 180 90 Q 140 45 76 90" fill="none" stroke="#10b981" strokeWidth="2.5" markerEnd="url(#arrowHeadGreen)" filter="url(#glowGreen)" />
              <line x1="140" y1="90" x2="68" y2="90" stroke="#10b981" strokeWidth="3" markerEnd="url(#arrowHeadGreen)" />
              {/* Shifted 3/4 Face Feature Guides */}
              <ellipse cx="132" cy="90" rx="36" ry="54" fill="none" stroke="#38bdf8" strokeWidth="1.2" strokeDasharray="2 2" />
              <circle cx="118" cy="82" r="3.5" fill="none" stroke="#e0f2fe" strokeWidth="1.5" />
              <circle cx="146" cy="80" r="3.5" fill="none" stroke="#e0f2fe" strokeWidth="1.5" />
              <text x="58" y="93" fill="#10b981" fontSize="10" fontWeight="800" textAnchor="end">
                ◄ TURN LEFT (+25°)
              </text>
            </g>
          )}

          {/* 5. POSE 4: TURN RIGHT (Yaw -25°) */}
          {isRight && (
            <g>
              {/* Base Head Outline Profile Right */}
              <ellipse cx="130" cy="90" rx="46" ry="60" fill="rgba(14, 165, 233, 0.05)" stroke="#0ea5e9" strokeWidth="1.5" strokeDasharray="4 2" />
              {/* Rotation Sweep Arc & Vector Right */}
              <path d="M 100 90 Q 140 45 204 90" fill="none" stroke="#10b981" strokeWidth="2.5" markerEnd="url(#arrowHeadGreen)" filter="url(#glowGreen)" />
              <line x1="140" y1="90" x2="212" y2="90" stroke="#10b981" strokeWidth="3" markerEnd="url(#arrowHeadGreen)" />
              {/* Shifted 3/4 Face Feature Guides */}
              <ellipse cx="148" cy="90" rx="36" ry="54" fill="none" stroke="#38bdf8" strokeWidth="1.2" strokeDasharray="2 2" />
              <circle cx="134" cy="80" r="3.5" fill="none" stroke="#e0f2fe" strokeWidth="1.5" />
              <circle cx="162" cy="82" r="3.5" fill="none" stroke="#e0f2fe" strokeWidth="1.5" />
              <text x="222" y="93" fill="#10b981" fontSize="10" fontWeight="800" textAnchor="start">
                TURN RIGHT (-25°) ►
              </text>
            </g>
          )}

          {/* 6. POSE 5: DIAGONAL UP-LEFT (+20° Pitch, +25° Yaw) */}
          {isUpLeft && (
            <g>
              <ellipse cx="140" cy="90" rx="46" ry="58" fill="rgba(14, 165, 233, 0.04)" stroke="#0ea5e9" strokeWidth="1.5" strokeDasharray="4 2" />
              {/* Diagonal Vector Vector Up-Left */}
              <line x1="140" y1="90" x2="65" y2="35" stroke="#10b981" strokeWidth="3.5" markerEnd="url(#arrowHeadGreen)" filter="url(#glowGreen)" />
              <circle cx="65" cy="35" r="16" fill="rgba(16, 185, 129, 0.15)" stroke="#10b981" strokeWidth="1.5" strokeDasharray="3 2" />
              <text x="65" y="24" fill="#10b981" fontSize="9" fontWeight="800" textAnchor="middle">
                ↖ UP-LEFT (+20°, +25°)
              </text>
            </g>
          )}

          {/* 7. POSE 6: DIAGONAL UP-RIGHT (+20° Pitch, -25° Yaw) */}
          {isUpRight && (
            <g>
              <ellipse cx="140" cy="90" rx="46" ry="58" fill="rgba(14, 165, 233, 0.04)" stroke="#0ea5e9" strokeWidth="1.5" strokeDasharray="4 2" />
              {/* Diagonal Vector Up-Right */}
              <line x1="140" y1="90" x2="215" y2="35" stroke="#10b981" strokeWidth="3.5" markerEnd="url(#arrowHeadGreen)" filter="url(#glowGreen)" />
              <circle cx="215" cy="35" r="16" fill="rgba(16, 185, 129, 0.15)" stroke="#10b981" strokeWidth="1.5" strokeDasharray="3 2" />
              <text x="215" y="24" fill="#10b981" fontSize="9" fontWeight="800" textAnchor="middle">
                UP-RIGHT (+20°, -25°) ↗
              </text>
            </g>
          )}

          {/* 8. POSE 7: DIAGONAL DOWN-LEFT (-20° Pitch, +25° Yaw) */}
          {isDownLeft && (
            <g>
              <ellipse cx="140" cy="90" rx="46" ry="58" fill="rgba(14, 165, 233, 0.04)" stroke="#0ea5e9" strokeWidth="1.5" strokeDasharray="4 2" />
              {/* Diagonal Vector Down-Left */}
              <line x1="140" y1="90" x2="65" y2="145" stroke="#10b981" strokeWidth="3.5" markerEnd="url(#arrowHeadGreen)" filter="url(#glowGreen)" />
              <circle cx="65" cy="145" r="16" fill="rgba(16, 185, 129, 0.15)" stroke="#10b981" strokeWidth="1.5" strokeDasharray="3 2" />
              <text x="65" y="168" fill="#10b981" fontSize="9" fontWeight="800" textAnchor="middle">
                ↙ DOWN-LEFT (-20°, +25°)
              </text>
            </g>
          )}

          {/* 9. POSE 8: DIAGONAL DOWN-RIGHT (-20° Pitch, -25° Yaw) */}
          {isDownRight && (
            <g>
              <ellipse cx="140" cy="90" rx="46" ry="58" fill="rgba(14, 165, 233, 0.04)" stroke="#0ea5e9" strokeWidth="1.5" strokeDasharray="4 2" />
              {/* Diagonal Vector Down-Right */}
              <line x1="140" y1="90" x2="215" y2="145" stroke="#10b981" strokeWidth="3.5" markerEnd="url(#arrowHeadGreen)" filter="url(#glowGreen)" />
              <circle cx="215" cy="145" r="16" fill="rgba(16, 185, 129, 0.15)" stroke="#10b981" strokeWidth="1.5" strokeDasharray="3 2" />
              <text x="215" y="168" fill="#10b981" fontSize="9" fontWeight="800" textAnchor="middle">
                DOWN-RIGHT (-20°, -25°) ↘
              </text>
            </g>
          )}
        </svg>
      </div>

      {/* Bottom Technical Calibration Bar */}
      <div className="diagram-calibration-footer">
        <div className="calibration-chip">
          <span className="calib-key">SENSOR:</span>
          <span className="calib-val">MediaPipe FaceMesh</span>
        </div>
        <div className="calibration-chip">
          <span className="calib-key">FRAME:</span>
          <span className="calib-val">Biometric Oval</span>
        </div>
        <div className="calibration-chip">
          <span className="calib-key">DISTANCE:</span>
          <span className="calib-val">45–60 cm</span>
        </div>
        <div className="calibration-chip">
          <span className="calib-key">TOLERANCE:</span>
          <span className="calib-val">±15° Degree Arc</span>
        </div>
      </div>
    </div>
  );
}

